// Pure helpers for the HR invoice review flow (labels, banner copy, button
// eligibility). Kept free of React/moment so they can be unit tested on their own.

export const DISPUTE_REASONS = [
  { value: 'not_our_employee', label: 'Not our employee' },
  { value: 'left_before_service', label: 'Left before service date' },
  { value: 'wrong_price', label: 'Wrong price' },
  { value: 'not_availed', label: 'Not availed' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'already_billed', label: 'Already billed or paid' },
  { value: 'other', label: 'Other' },
]

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Date-only strings are read as calendar dates (new Date('2026-11-05') would be
// UTC midnight and can shift a day); timestamps are read in the viewer's zone.
const parseDate = (value) => {
  if (!value) return null
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const date = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(value)
  return isNaN(date.getTime()) ? null : date
}

// formatReviewDate('2026-11-05') => '05 Nov 2026'; withYear false => '05 Nov'
export const formatReviewDate = (value, withYear = true) => {
  const date = parseDate(value)
  if (!date) return value || ''
  const day = String(date.getDate()).padStart(2, '0')
  return `${day} ${MONTHS[date.getMonth()]}${withYear ? ` ${date.getFullYear()}` : ''}`
}

// Tailwind classes per badge tone (shared by the list and the participation page)
export const BADGE_TONE_CLASSES = {
  amber: 'text-ds-amber-dk bg-ds-amber-lt',
  green: 'text-ds-green-dk bg-ds-green-lt',
  rose: 'text-ds-rose-dk bg-ds-rose-lt',
}

export const isUnderReview = (review) => !!review && review.status === 'pending'

// Status-cell badge on the invoices list; null falls back to invoice_status.
export const reviewBadge = (review) => {
  if (!review) return null
  switch (review.status) {
    case 'pending':
      return { text: `Awaiting your review · due ${formatReviewDate(review.review_due_at, false)}`, tone: 'amber' }
    case 'approved':
      return { text: 'Approved', tone: 'green' }
    case 'auto_approved':
      return { text: 'Auto-approved', tone: 'green' }
    case 'disputed':
      return { text: 'Disputed — with ekincare finance', tone: 'rose' }
    default:
      return null
  }
}

const normalise = (value) => (value || '').trim().toLowerCase()

// Badge to show instead of invoice_status: only while the item is still with HR
// ("With HR"), back with finance after a dispute ("Draft"), or pending. After release
// the review keeps its decision for good, so the normal invoice_status takes over.
// The detail endpoint sends no invoice_status for a draft, so a disputed item with
// none is the draft the dispute made, and keeps its badge.
export const listStatusBadge = (item = {}) => {
  const review = item.review
  if (!review) return null
  const status = normalise(item.invoice_status)
  const beforeRelease = ['with hr', 'draft'].includes(status) || review.status === 'pending' ||
    (review.status === 'disputed' && !status)
  return beforeRelease ? reviewBadge(review) : null
}

// Released = past the HR review step; master's confirm/dispute window applies again.
// Only an approval leads to release from the review. A dispute (or a take-back,
// "closed") puts the invoice back to draft, and the detail endpoint sends no
// invoice_status for a draft or for Approval Pending alike, so those stay unreleased.
export const isReleased = ({ review, invoiceStatus }) => (
  normalise(invoiceStatus) !== 'with hr' && (!review || ['approved', 'auto_approved'].includes(review.status))
)

export const approveSuccessMessage = (review) => (
  review && review.date_request && review.date_request.status === 'open'
    ? 'Approved — the invoice will be issued once ekincare finance answers your date request'
    : 'Approved — the invoice will be issued and mailed to you shortly'
)

export const dateRequestText = (dateRequest) => {
  if (!dateRequest) return ''
  if (dateRequest.status === 'open') return `Date change to ${formatReviewDate(dateRequest.requested_date)} requested — waiting on ekincare finance`
  if (dateRequest.status === 'declined' || dateRequest.resolution === 'declined') return `Date change declined${dateRequest.note ? `: ${dateRequest.note}` : ''}`
  if (dateRequest.resolution === 'date_applied') return 'Date change agreed'
  return ''
}

export const reviewBanner = (review) => {
  if (!isUnderReview(review)) return null
  return {
    due: `Please review this invoice by ${formatReviewDate(review.review_due_at)}. If we hear nothing by then it will be issued as it stands.`,
    invoiceDate: review.invoice_date ? `Invoice date: ${formatReviewDate(review.invoice_date)}` : '',
    dateRequest: dateRequestText(review.date_request),
  }
}

// Same role rules as the legacy confirm/dispute buttons: staff only, disabled for sales.
export const reviewActions = ({ review, role = '', salesRole = null }) => {
  const visible = isUnderReview(review) && (role || '').toLowerCase() === 'staff'
  const dateRequestOpen = !!(review && review.date_request && review.date_request.status === 'open')
  return {
    visible,
    disabled: salesRole === 'sales',
    canRequestDate: visible && !dateRequestOpen,
  }
}

// Returns an error message, or '' when the dispute can be sent.
export const validateDispute = ({ reasonCode, comment }) => {
  if (!reasonCode) return 'Please choose a reason'
  if (reasonCode === 'other' && !(comment || '').trim()) return 'Please tell us what is wrong'
  return ''
}

export const disputePayload = ({ invoiceId, invoiceItemId, reasonCode, comment, scope, selectedIds = [] }) => ({
  company_invoice_id: invoiceId,
  company_invoice_item_id: invoiceItemId,
  reason_code: reasonCode,
  comment: (comment || '').trim(),
  invoice_assignment_ids: scope === 'selected' ? selectedIds : [],
})

// Adds or removes ids from the cross-page selection, keeping order and no duplicates.
export const toggleSelection = (selected, ids, checked) => {
  const present = ids.filter((id) => id !== undefined && id !== null)
  if (checked) return [...selected, ...present.filter((id) => !selected.includes(id))]
  return selected.filter((id) => !present.includes(id))
}

// 'DD-MM-YYYY' (the billed period start passed from the list) => 'YYYY-MM'
const billedMonth = (startDate) => {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(startDate || '')
  return match ? `${match[3]}-${match[2]}` : null
}

// Months HR may ask for: the billed month (review.billed_month, else the period
// start from the list) through the current month.
export const requestDateRange = ({ billedMonth: reviewBilledMonth, startDate } = {}, today = new Date()) => {
  const max = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  const min = (/^\d{4}-\d{2}$/.test(reviewBilledMonth || '') ? reviewBilledMonth : billedMonth(startDate)) || max
  return { min: min > max ? max : min, max }
}

export const isMonthSelectable = (month, { min, max }) => !!month && month >= min && month <= max

// Which participant layout the response is: appointment rows (PPU) or customer
// rows (subscription). Service labels come from benefit names ("Annual Health
// Checkup", "Online Consults", ...), so the data decides, not the label; null
// when neither key is present and the caller falls back to the label.
export const participantListKind = (data = {}) => {
  if (Array.isArray(data.appointments)) return 'ppu'
  if (Array.isArray(data.customers)) return 'subscription'
  return null
}

// One shape decision for the break-up table so its headers and rows always agree.
// Known labels keep their layout; anything else uses the participant list's kind,
// or failing that the break-up rows themselves (PPU rows carry a package_name).
export const breakUpShape = (service, kind = null, details = []) => {
  const label = normalise(service)
  if (['health checks', 'vaccination', 'vision', 'dental', 'onsite', 'engagement activity', 'ohc'].includes(label)) return 'ppu'
  // Gym lines are mostly wallet invoices with package rows, so the data decides their shape.
  if (['doctor consultation', 'opd', 'ewap', 'smart cover'].includes(label)) return 'subscription'
  if (label === 'health coach') return 'health_coach'
  if (label === 'employee addition') return 'employee_addition'
  if (kind) return kind
  if (!details || details.length === 0) return null
  return 'package_name' in details[0] ? 'ppu' : 'subscription'
}

export const serverMessage = (err, fallback) => (err && err.response && err.response.data && err.response.data.message) || fallback
